import styled from "@emotion/styled";
import { Table } from "@tanstack/react-table";
import { CloseIcon } from "components/Icons";
import { Filter, FiltersInterface } from "config/consts";
import { AnimatePresence } from "framer-motion";
import { FILTERS_INITIAL_STATE } from "pages/TradeHistory/Activity";
import React, { useRef } from "react";
import { TransactionDetail } from "types/history";
import "../../styles/transitions.css";
import { AmountFilter } from "./AmountFilter";
import { NetworkFilter } from "./NetworkFilter";
import { TransactionFilter } from "./TransactionFilter";
import { ClearButton } from "./elements/ClearButton";
import { FilterContainer } from "./elements/FilterContainer";
import { css } from "@emotion/react";
import { TokenFilter } from "./TokenFilter";
import { useIsMobile } from "../../hooks/useDeviceType";

interface FilterBarProps {
  table: Table<TransactionDetail>;
  confirmedFilters: FiltersInterface;
  setConfirmedFilters: React.Dispatch<React.SetStateAction<FiltersInterface>>;
  scrollToListTop: () => void;
}

export const FilterBar = ({
  table,
  confirmedFilters,
  setConfirmedFilters,
  scrollToListTop,
}: FilterBarProps) => {
  const hasFilters = Object.values(confirmedFilters).some(value => value != null);
  const nodeRef = useRef(null);
  const isMobile = useIsMobile();
  const updateOptions =
    (filterType: Filter) => (updatedOptions: FiltersInterface[keyof FiltersInterface]) => {
      setConfirmedFilters((s: FiltersInterface) => {
        return { ...s, [filterType]: updatedOptions };
      });
    };

  return (
    <FilterContainer>
      <ScrollableContainer>
        <AnimatePresence>
          {hasFilters && (
            <ClearButton
              variants={fadeVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{
                appear: { duration: 0.5 },
                enter: { duration: 0.3 },
                exit: { duration: 0.3 },
              }}
              ref={nodeRef}
              onClick={() => {
                table.resetColumnFilters();
                setConfirmedFilters(FILTERS_INITIAL_STATE);
                scrollToListTop();
              }}
            >
              Clear all{" "}
              <CloseIconStyled
                css={css`
                  margin-bottom: 0;
                `}
              />
            </ClearButton>
          )}
        </AnimatePresence>
        <NetworkFilter
          table={table}
          confirmedOptions={confirmedFilters[Filter.Network]}
          setConfirmedOptions={updateOptions(Filter.Network)}
        />
        <TransactionFilter
          table={table}
          confirmedOptions={confirmedFilters[Filter.Transaction]}
          setConfirmedOptions={updateOptions(Filter.Transaction)}
        />
        {/*Disable until we figure out if we're going to allow people to filter by token*/}
        <AmountFilter
          table={table}
          confirmedOptions={confirmedFilters[Filter.Amount]}
          setConfirmedOptions={updateOptions(Filter.Amount)}
        />
      </ScrollableContainer>
      <TokenFilter
        css={css`
          display: ${isMobile ? "block" : "flex"};
          flex-grow: 1;
        `}
        table={table}
        confirmedOptions={confirmedFilters[Filter.Token]}
        setConfirmedOptions={updateOptions(Filter.Token)}
      />
      {/* TODO: Add date filter later on */}
      {/* <DateFilter
        filterDropdownShown={filterDropdownShown}
        setFilterDropdownShown={setFilterDropdownShown}
      /> */}
    </FilterContainer>
  );
};

// Sub-Components

const CloseIconStyled = styled(CloseIcon)`
  margin-bottom: 2px;
`;

const ScrollableContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;
  scrollbar-width: none;
  max-width: 100vw;
  padding-bottom: 0;
  height: 45px;
  align-items: center;
`;
export const fadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};
